import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useQuery } from '../hooks/useQuery';
import { useMutation } from '../hooks/useMutation';
import { useToast } from '../hooks/useToast';
import { useConfirm } from '../hooks/useConfirm';
import { useTimesheetEdit } from '../hooks/useTimesheetEdit';
import { useLeaveActions } from '../hooks/useLeaveActions';
import * as XLSX from 'xlsx';
import {
  Calendar, User, Clock,
  Filter, Download, Plus, X, CheckCircle2, XCircle,
  RefreshCw, Check, Sliders
} from 'lucide-react';
import { PageHeader, Card, StatusBadge, LoadingSpinner, EmptyState } from '../components/ui';
import { QuotasModal } from '../components/QuotasModal';
import { LeaveBalancePanel } from '../components/LeaveBalancePanel';
import { EmployeeBalanceCard } from '../components/EmployeeBalanceCard';
import { useLeaveQuotas } from '../hooks/useLeaveQuotas';
import { computeEmployeeBalances, DISPLAYED_LEAVE_TYPES, type LeaveRecord } from '../utils/leaveBalanceUtils';

// --- TYPES ---
interface LeaveRequest {
  id: string;
  created_at: string;
  start_date: string;
  end_date: string;
  type: string;
  reason: string | null;
  status: string;
  rejection_reason?: string | null;
  employee: { full_name: string } | null;
  employee_id: string;
}

interface Employee {
  id: string;
  full_name: string;
  role: string;
}

// --- CONSTANTES ---
const LEAVE_LABELS: Record<string, string> = {
  cp: 'Congés Payés',
  maladie: 'Maladie',
  sans_solde: 'Sans Solde',
  formation: 'Formation',
  autre: 'Autre'
};

const LEAVE_COLORS: Record<string, string> = {
  cp: '#10b981',
  maladie: '#ef4444',
  sans_solde: '#64748b',
  formation: '#8b5cf6',
  autre: '#f59e0b'
};

const STATUS_LABELS: Record<string, string> = {
  pending: 'En attente',
  validated: 'Validé',
  rejected: 'Refusé'
};

// Calculer jours ouvrés entre deux dates
const countBusinessDays = (startStr: string, endStr: string): number => {
  const start = new Date(startStr);
  const end = new Date(endStr);
  let days = 0;
  const d = new Date(start);
  while (d <= end) {
    const dow = d.getDay();
    if (dow !== 0 && dow !== 6) days++;
    d.setDate(d.getDate() + 1);
  }
  return days;
};

export default function SecretariatLeaves() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { confirm: confirmDialog } = useConfirm();
  const { deleteLeave } = useTimesheetEdit();
  const { validateLeave, rejectLeave } = useLeaveActions();

  // --- DATA ---
  const { data: leaves, loading, refresh: refreshLeaves } = useQuery<LeaveRequest>(
    () => supabase
      .from('leaves')
      .select('*, employee:employees!employee_id(full_name)')
      .order('created_at', { ascending: false })
      .limit(500) as any,
    []
  );

  const { data: employees } = useQuery<Employee>(
    () => supabase
      .from('employees')
      .select('id, full_name, role')
      .eq('is_active', true)
      .order('full_name'),
    []
  );

  // Filtres
  const [filterEmployee, setFilterEmployee] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');

  // Modal création
  const [showModal, setShowModal] = useState(false);

  // Modal quotas
  const [showQuotasModal, setShowQuotasModal] = useState(false);

  // Quotas + leaves pour le calcul des soldes (hook dédié)
  const { quotas: quotasData, leaves: allLeavesForBalance } = useLeaveQuotas();

  // Employé sélectionné pour le détail (modal)
  const [detailEmployee, setDetailEmployee] = useState<string | null>(null);

  // Recherche dans la grille des employés (utile si l'équipe est grande)
  const [balanceSearch, setBalanceSearch] = useState('');

  // Cards employés : tous les actifs, pré-calcul des soldes
  const employeeBalances = useMemo(() => {
    return employees
      .filter(emp => emp.full_name.toLowerCase().includes(balanceSearch.toLowerCase()))
      .map(emp => ({
        id: emp.id,
        name: emp.full_name,
        balances: computeEmployeeBalances(
          allLeavesForBalance as LeaveRecord[],
          quotasData,
          emp.id,
          DISPLAYED_LEAVE_TYPES,
        ),
      }));
  }, [employees, balanceSearch, allLeavesForBalance, quotasData]);

  // Soldes de l'employé en détail (pour modal)
  const detailBalances = useMemo(() => {
    if (!detailEmployee) return null;
    return employeeBalances.find(e => e.id === detailEmployee)?.balances ?? null;
  }, [detailEmployee, employeeBalances]);
  const [newLeave, setNewLeave] = useState({
    employee_id: '',
    type: 'cp',
    start_date: '',
    end_date: '',
    reason: '',
    validateDirectly: false
  });

  // --- FILTRAGE ---
  const filteredLeaves = useMemo(() => {
    return leaves.filter(leave => {
      if (filterEmployee !== 'all' && leave.employee_id !== filterEmployee) return false;
      if (filterType !== 'all' && leave.type !== filterType) return false;
      if (filterStatus !== 'all' && leave.status !== filterStatus) return false;
      if (filterDateStart && leave.start_date < filterDateStart) return false;
      if (filterDateEnd && leave.end_date > filterDateEnd) return false;
      return true;
    });
  }, [leaves, filterEmployee, filterType, filterStatus, filterDateStart, filterDateEnd]);

  // --- STATS ---
  const stats = useMemo(() => ({
    total: filteredLeaves.length,
    pending: filteredLeaves.filter(l => l.status === 'pending').length,
    validated: filteredLeaves.filter(l => l.status === 'validated').length,
    rejected: filteredLeaves.filter(l => l.status === 'rejected').length,
  }), [filteredLeaves]);

  // --- ACTIONS ---
  // Déléguée à useLeaveActions : audit DB (validated_by/at, rejected_by/at),
  // audit trail payroll_corrections, notifications employé + direction (JLO).
  const handleAction = async (id: string, newStatus: 'validated' | 'rejected') => {
    if (newStatus === 'rejected') {
      const result = await confirmDialog({
        title: "Motif du refus",
        input: true,
        variant: "warning",
        inputPlaceholder: "Motif du refus (obligatoire)...",
      });
      if (result === null || result === false) return;
      const reason = String(result).trim();
      if (!reason) {
        toast.warning("Un motif est requis.");
        return;
      }
      const ok = await rejectLeave(id, reason);
      if (ok) await refreshLeaves();
      return;
    }

    const confirmed = await confirmDialog({
      title: "Valider cette absence ?",
      message: "L'employé et la direction seront notifiés.",
      variant: "info",
      confirmLabel: "Valider",
    });
    if (!confirmed) return;
    const ok = await validateLeave(id);
    if (ok) await refreshLeaves();
  };

  const handleDelete = async (leave: LeaveRequest) => {
    const isValidated = leave.status === 'validated';

    // Congé validé : motif obligatoire + avertissement traçabilité
    if (isValidated) {
      const result = await confirmDialog({
        title: "Supprimer ce congé validé ?",
        message: "Ce congé a été validé. La suppression sera tracée dans l'historique et l'employé sera notifié.",
        input: true,
        inputPlaceholder: "Motif de la suppression (obligatoire)...",
        variant: "danger",
        confirmLabel: "Supprimer",
      });
      if (result === null || result === false) return;
      const reason = String(result).trim();
      if (!reason) {
        toast.warning("Un motif est requis pour supprimer un congé validé.");
        return;
      }
      const ok = await deleteLeave(leave.id, { reason, actorPage: 'secretariat_payroll' });
      if (ok) await refreshLeaves();
      return;
    }

    // Congé non validé : confirmation simple
    const ok = await confirmDialog({
      title: "Supprimer cette demande ?",
      message: "Cette action est irréversible.",
      variant: "danger",
    });
    if (!ok) return;
    const done = await deleteLeave(leave.id, { actorPage: 'secretariat_payroll' });
    if (done) await refreshLeaves();
  };

  // --- CRÉATION ---
  const { execute: createLeave, loading: submitting } = useMutation(
    (payload: { employee_id: string; type: string; start_date: string; end_date: string; reason: string | null; status: string }) =>
      supabase.from('leaves').insert(payload),
    { onSuccess: refreshLeaves }
  );

  const handleCreate = async () => {
    if (!newLeave.employee_id || !newLeave.start_date || !newLeave.end_date) {
      toast.warning("Veuillez remplir les champs obligatoires (Employé, Date début, Date fin).");
      return;
    }
    if (newLeave.start_date > newLeave.end_date) {
      toast.warning("La date de début doit être antérieure à la date de fin.");
      return;
    }

    const ok = await createLeave({
      employee_id: newLeave.employee_id,
      type: newLeave.type,
      start_date: newLeave.start_date,
      end_date: newLeave.end_date,
      reason: newLeave.reason || null,
      status: newLeave.validateDirectly ? 'validated' : 'pending'
    });

    if (ok) {
      setShowModal(false);
      setNewLeave({ employee_id: '', type: 'cp', start_date: '', end_date: '', reason: '', validateDirectly: false });
    }
  };

  // --- EXPORT XLSX ---
  const exportXLSX = () => {
    const data = filteredLeaves.map(l => ({
      'Employé': l.employee?.full_name || '—',
      'Type': LEAVE_LABELS[l.type] || l.type,
      'Du': new Date(l.start_date).toLocaleDateString('fr-FR'),
      'Au': new Date(l.end_date).toLocaleDateString('fr-FR'),
      'Nb Jours': countBusinessDays(l.start_date, l.end_date),
      'Statut': STATUS_LABELS[l.status] || l.status,
      'Motif': l.reason || '',
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Congés");
    ws['!cols'] = [
      { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 10 }, { wch: 12 }, { wch: 30 }
    ];
    XLSX.writeFile(wb, `conges_export_${new Date().toISOString().slice(0, 10)}.xlsx`);
  };

  // --- RENDER ---
  const selectStyle: React.CSSProperties = {
    padding: '8px 12px', borderRadius: '10px', border: '1px solid #e2e8f0',
    fontSize: '0.85rem', color: '#334155', background: 'white', cursor: 'pointer',
    appearance: 'none' as const, paddingRight: '28px',
    backgroundImage: 'url("data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' width=\'12\' height=\'12\' fill=\'%2394a3b8\' viewBox=\'0 0 16 16\'%3E%3Cpath d=\'M8 11L3 6h10z\'/%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat', backgroundPosition: 'right 8px center'
  };

  const inputStyle: React.CSSProperties = {
    padding: '8px 12px', borderRadius: '10px', border: '1px solid #e2e8f0',
    fontSize: '0.85rem', color: '#334155', background: 'white'
  };

  return (
    <div style={{ fontFamily: "'Inter', sans-serif" }}>

      {/* HEADER */}
      <PageHeader
        title="Gestion des Congés"
        subtitle="Saisie, suivi et validation des absences"
        onBack={() => navigate('/secretariat')}
        actions={
          <>
            <button
              onClick={() => setShowModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 18px', borderRadius: '12px', border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                color: 'white', fontWeight: '600', fontSize: '0.85rem', cursor: 'pointer',
                boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
              }}
            >
              <Plus size={18} /> Nouvelle demande
            </button>
            <button
              onClick={() => setShowQuotasModal(true)}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 18px', borderRadius: '12px', border: '1px solid #e2e8f0',
                background: 'white', color: '#475569', fontWeight: '500', fontSize: '0.85rem', cursor: 'pointer'
              }}
            >
              <Sliders size={16} /> Quotas
            </button>
            <button
              onClick={exportXLSX}
              style={{
                display: 'flex', alignItems: 'center', gap: '6px',
                padding: '10px 18px', borderRadius: '12px', border: '1px solid #e2e8f0',
                background: 'white', color: '#475569', fontWeight: '500', fontSize: '0.85rem', cursor: 'pointer'
              }}
            >
              <Download size={16} /> Excel
            </button>
            <button
              onClick={refreshLeaves}
              style={{
                padding: '10px', borderRadius: '12px', border: '1px solid #e2e8f0',
                background: 'white', color: '#64748b', cursor: 'pointer', display: 'flex', alignItems: 'center'
              }}
            >
              <RefreshCw size={16} />
            </button>
          </>
        }
      />

      {/* STATS */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
        {[
          { label: 'Total', value: stats.total, color: '#475569', bg: '#f1f5f9' },
          { label: 'En attente', value: stats.pending, color: '#d97706', bg: '#fef3c7' },
          { label: 'Validés', value: stats.validated, color: '#16a34a', bg: '#dcfce7' },
          { label: 'Refusés', value: stats.rejected, color: '#dc2626', bg: '#fee2e2' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'white', borderRadius: '12px', border: '1px solid #e2e8f0',
            padding: '16px', textAlign: 'center'
          }}>
            <div style={{ fontSize: '1.8rem', fontWeight: '800', color: s.color }}>{s.value}</div>
            <div style={{ fontSize: '0.75rem', fontWeight: '600', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* TABLEAU DE BORD SOLDES — grille de cards par employé pour voir
          d'un coup d'œil qui est proche de dépasser son quota. */}
      <Card padding="md" style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '14px', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontSize: '0.95rem', fontWeight: 700, color: '#0f172a' }}>
              Soldes congés de l'équipe
            </div>
            <div style={{ fontSize: '0.78rem', color: '#64748b', marginTop: '2px' }}>
              Cliquez sur un employé pour voir le détail · Barre hachée = en attente de validation
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px', alignItems: 'center' }}>
            <input
              type="text"
              placeholder="Rechercher…"
              value={balanceSearch}
              onChange={e => setBalanceSearch(e.target.value)}
              style={{
                padding: '8px 12px', borderRadius: '10px', border: '1px solid #e2e8f0',
                background: 'white', fontSize: '0.85rem', width: '180px',
              }}
            />
          </div>
        </div>
        {employeeBalances.length === 0 ? (
          <div style={{ padding: '24px', color: '#94a3b8', fontSize: '0.9rem', fontStyle: 'italic', textAlign: 'center' }}>
            Aucun employé {balanceSearch && 'ne correspond à la recherche'}
          </div>
        ) : (
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '14px',
          }}>
            {employeeBalances.map(emp => (
              <EmployeeBalanceCard
                key={emp.id}
                employeeId={emp.id}
                employeeName={emp.name}
                balances={emp.balances}
                onClick={() => setDetailEmployee(emp.id)}
              />
            ))}
          </div>
        )}
      </Card>

      {/* MODAL DETAIL SOLDE d'un employé */}
      {detailEmployee && detailBalances && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.6)', backdropFilter: 'blur(4px)',
          zIndex: 1000, display: 'flex', justifyContent: 'center', alignItems: 'flex-start', padding: '40px 20px',
          overflowY: 'auto',
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', width: '100%', maxWidth: '640px',
            padding: '24px 28px', boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '20px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '1.25rem', color: '#0f172a', fontWeight: 700 }}>
                  {employees.find(e => e.id === detailEmployee)?.full_name ?? 'Employé'}
                </h3>
                <p style={{ margin: '4px 0 0', fontSize: '0.85rem', color: '#64748b' }}>
                  Détail des soldes par type de congé
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => { setShowQuotasModal(true); setDetailEmployee(null); }}
                  style={{
                    padding: '7px 14px', borderRadius: '10px', border: '1px solid #c4b5fd',
                    background: '#f3e8ff', color: '#6d28d9', fontSize: '0.82rem',
                    fontWeight: 600, cursor: 'pointer',
                  }}
                >
                  Gérer les quotas
                </button>
                <button
                  onClick={() => setDetailEmployee(null)}
                  style={{
                    padding: '7px', borderRadius: '10px', border: '1px solid #e2e8f0',
                    background: 'white', color: '#64748b', cursor: 'pointer',
                    display: 'flex', alignItems: 'center',
                  }}
                  aria-label="Fermer"
                >
                  <X size={18} />
                </button>
              </div>
            </div>
            <LeaveBalancePanel balances={detailBalances} />
          </div>
        </div>
      )}

      {/* Modal Quotas */}
      <QuotasModal
        isOpen={showQuotasModal}
        onClose={() => setShowQuotasModal(false)}
        employees={employees}
      />

      {/* FILTRES */}
      <Card padding="md" style={{
        marginBottom: '20px',
        display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center'
      }}>
        <Filter size={16} color="#94a3b8" />

        <select value={filterEmployee} onChange={e => setFilterEmployee(e.target.value)} style={selectStyle}>
          <option value="all">Tous les employés</option>
          {employees.map(emp => (
            <option key={emp.id} value={emp.id}>{emp.full_name}</option>
          ))}
        </select>

        <select value={filterType} onChange={e => setFilterType(e.target.value)} style={selectStyle}>
          <option value="all">Tous les types</option>
          {Object.entries(LEAVE_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} style={selectStyle}>
          <option value="all">Tous les statuts</option>
          {Object.entries(STATUS_LABELS).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>

        <input type="date" value={filterDateStart} onChange={e => setFilterDateStart(e.target.value)} style={inputStyle} />
        <input type="date" value={filterDateEnd} onChange={e => setFilterDateEnd(e.target.value)} style={inputStyle} />

        {(filterEmployee !== 'all' || filterType !== 'all' || filterStatus !== 'all' || filterDateStart || filterDateEnd) && (
          <button
            onClick={() => { setFilterEmployee('all'); setFilterType('all'); setFilterStatus('all'); setFilterDateStart(''); setFilterDateEnd(''); }}
            style={{
              padding: '6px 12px', borderRadius: '8px', border: 'none',
              background: '#fee2e2', color: '#dc2626', fontSize: '0.8rem', fontWeight: '600', cursor: 'pointer'
            }}
          >
            Réinitialiser
          </button>
        )}
      </Card>

      {/* TABLEAU */}
      <Card padding="none" shadow="md">
        {loading ? (
          <LoadingSpinner message="Chargement..." />
        ) : filteredLeaves.length === 0 ? (
          <EmptyState icon={<Calendar size={32} />} message="Aucune demande trouvée" />
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Employé', 'Type', 'Du', 'Au', 'Jours', 'Statut', 'Motif', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '12px 14px', textAlign: 'left', color: '#64748b',
                    fontWeight: '600', fontSize: '0.75rem', textTransform: 'uppercase',
                    letterSpacing: '0.05em', borderBottom: '1px solid #e2e8f0'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredLeaves.map(leave => {
                const days = countBusinessDays(leave.start_date, leave.end_date);
                return (
                  <tr key={leave.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                    <td style={{ padding: '12px 14px', fontWeight: '600', color: '#1e293b' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <User size={14} color="#94a3b8" />
                        {leave.employee?.full_name || '—'}
                      </div>
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '6px',
                        padding: '4px 10px', borderRadius: '20px', fontSize: '0.78rem', fontWeight: '600',
                        background: `${LEAVE_COLORS[leave.type]}15`, color: LEAVE_COLORS[leave.type]
                      }}>
                        <span style={{
                          width: '6px', height: '6px', borderRadius: '50%',
                          background: LEAVE_COLORS[leave.type]
                        }} />
                        {LEAVE_LABELS[leave.type] || leave.type}
                      </span>
                    </td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>
                      {new Date(leave.start_date).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ padding: '12px 14px', color: '#475569' }}>
                      {new Date(leave.end_date).toLocaleDateString('fr-FR')}
                    </td>
                    <td style={{ padding: '12px 14px', fontWeight: '700', color: '#1e293b', textAlign: 'center' }}>
                      {days}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <StatusBadge status={leave.status} />
                    </td>
                    <td style={{ padding: '12px 14px', color: '#64748b', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {leave.reason || '—'}
                      {leave.rejection_reason && (
                        <div style={{ fontSize: '0.75rem', color: '#dc2626', marginTop: '2px' }}>
                          Refus: {leave.rejection_reason}
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '12px 14px' }}>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        {leave.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleAction(leave.id, 'validated')}
                              title="Valider"
                              style={{
                                padding: '6px', borderRadius: '8px', border: 'none',
                                background: '#dcfce7', color: '#16a34a', cursor: 'pointer',
                                display: 'flex', alignItems: 'center'
                              }}
                            >
                              <CheckCircle2 size={16} />
                            </button>
                            <button
                              onClick={() => handleAction(leave.id, 'rejected')}
                              title="Refuser"
                              style={{
                                padding: '6px', borderRadius: '8px', border: 'none',
                                background: '#fee2e2', color: '#dc2626', cursor: 'pointer',
                                display: 'flex', alignItems: 'center'
                              }}
                            >
                              <XCircle size={16} />
                            </button>
                          </>
                        )}
                        <button
                          onClick={() => handleDelete(leave)}
                          title="Supprimer"
                          style={{
                            padding: '6px', borderRadius: '8px', border: 'none',
                            background: '#f1f5f9', color: '#94a3b8', cursor: 'pointer',
                            display: 'flex', alignItems: 'center'
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </Card>

      {/* MODAL CRÉATION */}
      {showModal && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(4px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000
        }}>
          <div style={{
            background: 'white', borderRadius: '20px', width: '100%', maxWidth: '500px',
            padding: '32px', boxShadow: '0 20px 60px rgba(0,0,0,0.15)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: 0, fontSize: '1.2rem', fontWeight: '700', color: '#1e293b' }}>
                Nouvelle demande d'absence
              </h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  background: '#f1f5f9', border: 'none', padding: '8px', borderRadius: '10px',
                  cursor: 'pointer', color: '#64748b', display: 'flex'
                }}
              >
                <X size={18} />
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Employé */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                  Employé *
                </label>
                <select
                  value={newLeave.employee_id}
                  onChange={e => setNewLeave(prev => ({ ...prev, employee_id: e.target.value }))}
                  style={{ ...selectStyle, width: '100%', padding: '12px 14px' }}
                >
                  <option value="">Sélectionner un employé</option>
                  {employees.map(emp => (
                    <option key={emp.id} value={emp.id}>{emp.full_name}</option>
                  ))}
                </select>
              </div>

              {/* Type */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                  Type d'absence *
                </label>
                <select
                  value={newLeave.type}
                  onChange={e => setNewLeave(prev => ({ ...prev, type: e.target.value }))}
                  style={{ ...selectStyle, width: '100%', padding: '12px 14px' }}
                >
                  {Object.entries(LEAVE_LABELS).map(([k, v]) => (
                    <option key={k} value={k}>{v}</option>
                  ))}
                </select>
              </div>

              {/* Dates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                    Date début *
                  </label>
                  <input
                    type="date"
                    value={newLeave.start_date}
                    onChange={e => setNewLeave(prev => ({ ...prev, start_date: e.target.value }))}
                    style={{ ...inputStyle, width: '100%', padding: '12px 14px' }}
                  />
                </div>
                <div>
                  <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                    Date fin *
                  </label>
                  <input
                    type="date"
                    value={newLeave.end_date}
                    onChange={e => setNewLeave(prev => ({ ...prev, end_date: e.target.value }))}
                    style={{ ...inputStyle, width: '100%', padding: '12px 14px' }}
                  />
                </div>
              </div>

              {/* Jours calculés */}
              {newLeave.start_date && newLeave.end_date && newLeave.start_date <= newLeave.end_date && (
                <div style={{
                  background: '#f0fdf4', padding: '10px 14px', borderRadius: '10px',
                  display: 'flex', alignItems: 'center', gap: '8px',
                  color: '#16a34a', fontSize: '0.85rem', fontWeight: '600'
                }}>
                  <Clock size={16} />
                  {countBusinessDays(newLeave.start_date, newLeave.end_date)} jour(s) ouvré(s)
                </div>
              )}

              {/* Motif */}
              <div>
                <label style={{ fontSize: '0.8rem', fontWeight: '600', color: '#475569', marginBottom: '6px', display: 'block' }}>
                  Motif (facultatif)
                </label>
                <textarea
                  value={newLeave.reason}
                  onChange={e => setNewLeave(prev => ({ ...prev, reason: e.target.value }))}
                  rows={3}
                  placeholder="Précisez le motif si nécessaire..."
                  style={{
                    ...inputStyle, width: '100%', padding: '12px 14px',
                    resize: 'vertical', fontFamily: 'inherit'
                  }}
                />
              </div>

              {/* Valider directement */}
              <label style={{
                display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer',
                padding: '12px 14px', borderRadius: '12px',
                background: newLeave.validateDirectly ? '#f0fdf4' : '#f8fafc',
                border: `1px solid ${newLeave.validateDirectly ? '#86efac' : '#e2e8f0'}`,
                transition: 'all 0.2s'
              }}>
                <div
                  onClick={() => setNewLeave(prev => ({ ...prev, validateDirectly: !prev.validateDirectly }))}
                  style={{
                    width: '20px', height: '20px', borderRadius: '6px',
                    border: `2px solid ${newLeave.validateDirectly ? '#16a34a' : '#cbd5e1'}`,
                    background: newLeave.validateDirectly ? '#16a34a' : 'white',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    cursor: 'pointer', transition: 'all 0.2s', flexShrink: 0
                  }}
                >
                  {newLeave.validateDirectly && <Check size={14} color="white" />}
                </div>
                <div>
                  <div style={{ fontSize: '0.85rem', fontWeight: '600', color: '#1e293b' }}>
                    Valider directement
                  </div>
                  <div style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    La demande sera créée avec le statut "Validé"
                  </div>
                </div>
              </label>

              {/* Boutons */}
              <div style={{ display: 'flex', gap: '10px', marginTop: '8px' }}>
                <button
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0',
                    background: 'white', color: '#64748b', fontWeight: '600', cursor: 'pointer',
                    fontSize: '0.9rem'
                  }}
                >
                  Annuler
                </button>
                <button
                  onClick={handleCreate}
                  disabled={submitting}
                  style={{
                    flex: 1, padding: '12px', borderRadius: '12px', border: 'none',
                    background: 'linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)',
                    color: 'white', fontWeight: '600', cursor: submitting ? 'wait' : 'pointer',
                    fontSize: '0.9rem', boxShadow: '0 2px 8px rgba(124, 58, 237, 0.3)'
                  }}
                >
                  {submitting ? 'Création...' : 'Créer la demande'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
